// Совместимость с API AngularJS 1.2 поверх нового ядра: код сайта (services.js, контроллеры,
// директивы) регистрируется через angular.module, как раньше. Здесь только то, что этот код вызывает.
import { toJson } from './filters';
import { copy } from './scope';
import { createHttp, createQ } from './http';
import { normalize } from './compile';
import { injectAngularCss } from './index';
import { reportError } from './report';

const ARROW_ARG = /^([^(]+?)=>/;
const FN_ARGS = /^[^(]*\(\s*([^)]*)\)/m;
const STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;

// Зависимости функции: массив ['$http', function ($http) {}], свойство $inject или имена параметров.
export function annotate(fn) {
    if (Array.isArray(fn)) {
        return { deps: fn.slice(0, -1), fn: fn[fn.length - 1] };
    }
    if (fn.$inject) {
        return { deps: fn.$inject, fn };
    }
    const source = fn.toString().replace(STRIP_COMMENTS, '');
    const match = source.match(ARROW_ARG) || source.match(FN_ARGS);
    const args = match ? match[1].split(',').map(arg => arg.trim().replace(/=.*$/, '').trim()).filter(Boolean) : [];
    return { deps: args, fn };
}

// Как в AngularJS 1.2: объект с числовым length обходится по индексам.
function isArrayLike(obj) {
    if (obj == null || obj === window) {
        return false;
    }
    const length = obj.length;
    if (obj.nodeType === 1 && length) {
        return true;
    }
    return typeof obj === 'string' || Array.isArray(obj) || length === 0
        || (typeof length === 'number' && length > 0 && (length - 1) in obj);
}

// angular.forEach из 1.2: колбэк получает (значение, ключ).
function forEach(obj, iterator, context) {
    if (obj) {
        if (isArrayLike(obj)) {
            for (let i = 0; i < obj.length; i++) {
                iterator.call(context, obj[i], i);
            }
        } else {
            Object.keys(obj).forEach(key => iterator.call(context, obj[key], key));
        }
    }
    return obj;
}

export function installAngularCompat(runtime, target = window) {
    injectAngularCss(target.document);
    const providers = new Map();
    const instances = new Map();
    const controllerDefs = new Map();
    const caches = {};

    // Значения зависимостей: сначала локальные ($scope, $element...), потом сервисы.
    const resolveArgs = (deps, locals) => deps.map(dep => (locals && Object.prototype.hasOwnProperty.call(locals, dep) ? locals[dep] : injector.get(dep)));

    const injector = {
        get(name) {
            if (instances.has(name)) {
                return instances.get(name);
            }
            const provider = providers.get(name);
            if (!provider) {
                throw new Error(`Неизвестный сервис: ${name}`);
            }
            let instance;
            if (provider.type === 'value') {
                instance = provider.def;
            } else if (provider.type === 'service') {
                instance = injector.instantiate(provider.def);
            } else {
                instance = injector.invoke(provider.def);
            }
            instances.set(name, instance);
            return instance;
        },
        invoke(def, self, locals) {
            const { deps, fn } = annotate(def);
            return fn.apply(self, resolveArgs(deps, locals));
        },
        instantiate(def, locals) {
            const { fn } = annotate(def);
            const instance = Object.create(fn.prototype || Object.prototype);
            const result = injector.invoke(def, instance, locals);
            return result !== null && (typeof result === 'object' || typeof result === 'function') ? result : instance;
        },
    };

    const $q = createQ(runtime);

    function $timeout(fn, delay = 0, invokeApply = true) {
        let resolveFn;
        let rejectFn;
        const promise = $q.when(new Promise((resolve, reject) => {
            resolveFn = resolve;
            rejectFn = reject;
        }));
        promise.$$timeoutId = setTimeout(() => {
            try {
                resolveFn(typeof fn === 'function' ? fn() : undefined);
            } catch (error) {
                rejectFn(error);
                reportError(error);
            }
            if (invokeApply !== false) {
                runtime.rootScope.$apply();
            }
        }, delay);
        promise.$$reject = rejectFn;
        return promise;
    }
    $timeout.cancel = promise => {
        if (promise && promise.$$timeoutId !== undefined) {
            clearTimeout(promise.$$timeoutId);
            promise.$$reject('canceled');
            return true;
        }
        return false;
    };

    function $interval(fn, delay, count = 0, invokeApply = true) {
        let iteration = 0;
        let rejectFn;
        const promise = $q.when(new Promise((resolve, reject) => {
            rejectFn = reject;
        }));
        const id = setInterval(() => {
            iteration++;
            try {
                fn(iteration);
            } catch (error) {
                reportError(error);
            }
            if (count > 0 && iteration >= count) {
                clearInterval(id);
            }
            if (invokeApply !== false) {
                runtime.rootScope.$apply();
            }
        }, delay);
        promise.$$intervalId = id;
        promise.$$reject = rejectFn;
        return promise;
    }
    $interval.cancel = promise => {
        if (promise && promise.$$intervalId !== undefined) {
            clearInterval(promise.$$intervalId);
            promise.$$reject('canceled');
            return true;
        }
        return false;
    };

    // Элемент для кода сайта — массив из одного узла: директивы берут element[0].
    const nodesOf = element => (element && element.nodeType ? [element] : Array.from(element || []));
    const builtins = {
        $rootScope: runtime.rootScope,
        $http: createHttp(runtime),
        $q,
        $timeout,
        $interval,
        $window: target,
        $document: [target.document],
        $animate: {
            addClass(element, className) {
                nodesOf(element).forEach(node => runtime.animate.addClass(node, className));
            },
            removeClass(element, className) {
                nodesOf(element).forEach(node => runtime.animate.removeClass(node, className));
            },
        },
        $compile: element => scope => nodesOf(element).forEach(node => runtime.compile(node, scope)),
        $filter: name => runtime.filters.get(name),
        $cacheFactory: id => {
            if (!caches[id]) {
                const store = new Map();
                caches[id] = {
                    put(key, value) {
                        if (value !== undefined) {
                            store.set(key, value);
                        }
                        return value;
                    },
                    get: key => store.get(key),
                    remove: key => store.delete(key),
                };
            }
            return caches[id];
        },
        $controller: (name, locals) => injector.instantiate(controllerDefs.get(name), locals),
    };
    Object.keys(builtins).forEach(name => instances.set(name, builtins[name]));

    // Определение директивы получается при первом обращении компилятора: к этому времени
    // зарегистрированы все сервисы страницы.
    function convertDirective(factory) {
        let resolved = null;
        const resolve = () => {
            if (!resolved) {
                let ddo = injector.invoke(factory);
                if (typeof ddo === 'function') {
                    ddo = { link: ddo };
                }
                resolved = {
                    restrict: ddo.restrict || 'A',
                    priority: ddo.priority || 0,
                    require: ddo.require,
                    template: ddo.template,
                    templateUrl: ddo.templateUrl,
                    link: ddo.link && ((scope, element, attrs, rt, required) => ddo.link(scope, [element], attrs, required)),
                };
            }
            return resolved;
        };
        const definition = {};
        ['restrict', 'priority', 'require', 'template', 'templateUrl', 'link'].forEach(key => {
            Object.defineProperty(definition, key, { enumerable: true, get: () => resolve()[key] });
        });
        return definition;
    }

    const module = {
        factory(name, def) {
            providers.set(name, { type: 'factory', def });
            return module;
        },
        service(name, def) {
            providers.set(name, { type: 'service', def });
            return module;
        },
        value(name, value) {
            providers.set(name, { type: 'value', def: value });
            instances.delete(name);
            return module;
        },
        controller(name, def) {
            controllerDefs.set(name, def);
            runtime.controllers.set(name, (scope, element, attrs) => injector.instantiate(def, {
                $scope: scope,
                $element: [element],
                $attrs: attrs,
            }));
            return module;
        },
        directive(name, factory) {
            runtime.directives.set(normalize(name), convertDirective(factory));
            return module;
        },
        filter(name, factory) {
            let filterFn = null;
            runtime.filters.register(name, (...args) => {
                if (!filterFn) {
                    filterFn = injector.invoke(factory);
                }
                return filterFn(...args);
            });
            return module;
        },
    };

    const angular = {
        // Все модули пишут в общий реестр: имена модулей и их зависимости ни на что не влияют.
        module: () => module,
        bind(self, fn, ...args) {
            return typeof fn === 'function' ? fn.bind(self, ...args) : fn;
        },
        forEach,
        copy,
        toJson,
        isString: value => typeof value === 'string',
        isNumber: value => typeof value === 'number',
        isObject: value => value !== null && typeof value === 'object',
        isArray: Array.isArray,
        // options.animations === false: классы меняются сразу, без анимаций.
        $$bootstrap(element, options = {}) {
            if (options.animations !== false) {
                runtime.rootScope.$$postDigest(() => runtime.animate.enable());
            }
            runtime.compile(element, runtime.rootScope);
            runtime.rootScope.$digest();
            return injector;
        },
    };

    target.angular = angular;
    runtime.injector = injector;
    return angular;
}
